import { Fragment, type ReactNode } from "react";
import { MD, useMediaQuery } from "./useMediaQuery";
import { ScrollArea } from "./ScrollArea";

export type Column<T> = {
  key: string;
  header: string;
  cell: (row: T) => ReactNode;
  primary?: boolean;
  hideOnCard?: boolean;
  className?: string;
};

type Props<T> = {
  rows: T[];
  columns: Column<T>[];
  rowKey: (row: T) => string;
  label: string;
  onSelect?: (row: T) => void;
  selectedKey?: string;
  empty?: ReactNode;
  /** Extra classes for the <table>; ignored on cards. */
  tableClassName?: string;
  /** Detail shown under a row: a full-width table row, or the end of the card. */
  expanded?: (row: T) => ReactNode;
};

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  label,
  onSelect,
  selectedKey,
  empty,
  tableClassName,
  expanded,
}: Props<T>) {
  const wide = useMediaQuery(MD);
  if (rows.length === 0 && empty !== undefined) return <>{empty}</>;

  const primary = columns.find((c) => c.primary) ?? columns[0];

  if (!wide) {
    const rest = columns.filter((c) => c !== primary && !c.hideOnCard);
    // A <button> only allows phrasing content, so selectable cards keep span lines.
    const Line = onSelect ? "span" : "div";
    return (
      <ul aria-label={label} className="divide-y divide-zinc-100">
        {rows.map((row) => {
          const key = rowKey(row);
          const selected = key === selectedKey;
          const body = (
            <>
              <Line className="block text-sm font-medium text-zinc-900">{primary?.cell(row)}</Line>
              {rest.map((c) => (
                <Line key={c.key} className="mt-1 block text-sm text-zinc-600">
                  {c.header ? <span className="text-zinc-500">{c.header}: </span> : null}
                  {c.cell(row)}
                </Line>
              ))}
            </>
          );
          const base = "rounded-md p-3";
          const tone = selected ? "bg-brand-light/15" : "";
          const extra = expanded?.(row);
          return (
            <li key={key}>
              {onSelect ? (
                <button
                  type="button"
                  aria-pressed={selected}
                  onClick={() => onSelect(row)}
                  className={`block w-full text-left min-h-10 ${base} ${tone}`}
                >
                  {body}
                </button>
              ) : (
                <div className={`${base} ${tone}`}>
                  {body}
                  {extra ? <div className="mt-3">{extra}</div> : null}
                </div>
              )}
              {onSelect && extra ? <div className="mt-2">{extra}</div> : null}
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <ScrollArea label={label}>
      <table aria-label={label} className={`w-full text-left text-sm ${tableClassName ?? ""}`.trim()}>
        <thead className="border-b border-zinc-200 bg-zinc-50 text-xs uppercase text-zinc-500">
          <tr>
            {columns.map((c) => (
              <th key={c.key} scope="col" className={`px-4 py-3 font-medium ${c.className ?? ""}`.trim()}>
                {c.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {rows.map((row) => {
            const key = rowKey(row);
            const selected = key === selectedKey;
            const extra = expanded?.(row);
            return (
              <Fragment key={key}>
                <tr
                  onClick={onSelect ? () => onSelect(row) : undefined}
                  className={selected ? "bg-brand-light/15" : onSelect ? "cursor-pointer hover:bg-zinc-50" : "hover:bg-zinc-50"}
                >
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-3 ${c.className ?? ""}`.trim()}>
                      {onSelect && c === primary ? (
                        <button type="button" aria-pressed={selected} className="text-left">
                          {c.cell(row)}
                        </button>
                      ) : (
                        c.cell(row)
                      )}
                    </td>
                  ))}
                </tr>
                {extra ? (
                  <tr>
                    <td colSpan={columns.length} className="bg-zinc-50 px-4 py-3">
                      {extra}
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </ScrollArea>
  );
}
